import { memo, useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { ContextMenu } from "@/components/common/ContextMenu";
import { Icon } from "@/components/common/Icon";
import { RichTooltip } from "@/components/common/RichTooltip";
import { BoundaryMarker } from "./BoundaryMarker";
import { formatTimecode } from "./formatTime";
import type { SegmentRange } from "./useSegmentation";

/** 時刻を 0-1 のタイムライン位置比率に変換する (duration<=0 のときは 0)。 */
function toRatio(time: number, duration: number): number {
	return duration > 0 ? Math.min(1, Math.max(0, time / duration)) : 0;
}

interface TimelineProps {
	containerRef: RefObject<HTMLDivElement | null>;
	duration: number;
	currentTime: number;
	boundaries: readonly number[];
	segments: SegmentRange[];
	selectedBoundary: number | null;
	selectedSegment: number | null;
	hoveredSegment: number | null;
	onAddBoundary: (timeSec: number) => void;
	onMoveBoundary: (index: number, timeSec: number) => void;
	onRemoveBoundary: (index: number) => void;
	onSelectBoundary: (index: number) => void;
	onSelectSegment: (index: number, start: number) => void;
	onHoverSegment: (index: number | null) => void;
	onDragStart: (index: number) => void;
	onDragEnd: () => void;
}

interface TrackProps {
	barRef: RefObject<HTMLDivElement | null>;
	duration: number;
	boundaries: readonly number[];
	segments: SegmentRange[];
	selectedBoundary: number | null;
	selectedSegment: number | null;
	hoveredSegment: number | null;
	onMoveBoundary: (index: number, relX: number) => void;
	onContextMenu: (index: number, x: number, y: number) => void;
	onSelectBoundary: (index: number) => void;
	onSelectSegment: (index: number, start: number) => void;
	onHoverSegment: (index: number | null) => void;
	onDragStart: (index: number) => void;
	onDragEnd: () => void;
}

/**
 * セグメント領域と工程分解点マーカーの描画を担う。再生ヘッドは currentTime が変わるたびに
 * 更新されるため、ここを memo で切り離して O(セグメント数) の再描画を再生中に発生させない。
 */
const Track = memo(function Track({
	barRef,
	duration,
	boundaries,
	segments,
	selectedBoundary,
	selectedSegment,
	hoveredSegment,
	onMoveBoundary,
	onContextMenu,
	onSelectBoundary,
	onSelectSegment,
	onHoverSegment,
	onDragStart,
	onDragEnd,
}: TrackProps) {
	return (
		<>
			{/* セグメント領域。区間は塗らずに工程分解点の線だけで分け、選択中・ホバー中の区間だけ色を付ける。 */}
			{segments.map((seg, i) => {
				const isHighlighted = selectedSegment === i || hoveredSegment === i;
				const segmentRatio = duration > 0 ? (seg.end - seg.start) / duration : 0;
				return (
					<div
						key={i}
						className="group absolute top-0 h-full cursor-pointer transition-[background-color,box-shadow]"
						style={{
							left: `${toRatio(seg.start, duration) * 100}%`,
							width: `${segmentRatio * 100}%`,
							backgroundColor: isHighlighted ? "var(--accent-subtle)" : undefined,
							boxShadow: isHighlighted ? "inset 0 0 0 1px var(--accent-line)" : undefined,
						}}
						onClick={(e) => {
							e.stopPropagation();
							onSelectSegment(i, seg.start);
						}}
						onMouseEnter={() => onHoverSegment(i)}
						onMouseLeave={() => onHoverSegment(null)}
						data-testid={`segment-region-${i}`}
					>
						<span
							className={`pointer-events-none absolute inset-0 flex items-center justify-center font-mono text-ui font-medium text-accent transition-opacity ${hoveredSegment === i ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
						>
							{i + 1}
						</span>
					</div>
				);
			})}

			{/* 工程分解点マーカー */}
			{boundaries.map((b, i) => (
				<BoundaryMarker
					key={i}
					position={toRatio(b, duration)}
					timeSec={b}
					index={i}
					selected={selectedBoundary === i}
					timelineRef={barRef}
					onMove={onMoveBoundary}
					onSelect={onSelectBoundary}
					onContextMenu={onContextMenu}
					onDragStart={onDragStart}
					onDragEnd={onDragEnd}
				/>
			))}
		</>
	);
});

export function Timeline({
	containerRef,
	duration,
	currentTime,
	boundaries,
	segments,
	selectedBoundary,
	selectedSegment,
	hoveredSegment,
	onAddBoundary,
	onMoveBoundary,
	onRemoveBoundary,
	onSelectBoundary,
	onSelectSegment,
	onHoverSegment,
	onDragStart,
	onDragEnd,
}: TimelineProps) {
	const barRef = useRef<HTMLDivElement>(null);
	// 削除対象は selectedBoundary で一本化するため、メニュー側は表示位置だけを持つ。
	const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

	useEffect(() => {
		setContextMenu(null);
	}, [boundaries]);

	const handleDoubleClick = useCallback(
		(e: React.MouseEvent) => {
			if (!barRef.current) return;
			const rect = barRef.current.getBoundingClientRect();
			const relX = (e.clientX - rect.left) / rect.width;
			const timeSec = relX * duration;
			onAddBoundary(timeSec);
		},
		[duration, onAddBoundary],
	);

	const handleMarkerMove = useCallback(
		(index: number, relX: number) => {
			onMoveBoundary(index, relX * duration);
		},
		[duration, onMoveBoundary],
	);
	const handleBoundaryContextMenu = useCallback(
		(index: number, x: number, y: number) => {
			onSelectBoundary(index);
			setContextMenu({ x, y });
		},
		[onSelectBoundary],
	);
	// マーカーの pointerdown は伝播を止めるため、ContextMenu が持つ外側クリック検知 (window)
	// が働かない。選択が切り替わったらここで閉じ、ハイライトと削除対象のズレを防ぐ。
	const handleSelectBoundary = useCallback(
		(index: number) => {
			setContextMenu(null);
			onSelectBoundary(index);
		},
		[onSelectBoundary],
	);
	const handleRemoveFromContextMenu = useCallback(() => {
		if (selectedBoundary === null) return;
		onRemoveBoundary(selectedBoundary);
		setContextMenu(null);
	}, [selectedBoundary, onRemoveBoundary]);
	const playheadPosition = toRatio(currentTime, duration);

	return (
		<div ref={containerRef} className="relative px-2 pb-1 pt-7">
			<span className="absolute right-2 top-1 z-20 inline-flex items-center gap-0.5">
				<span className="text-caption text-fg-3">操作方法</span>
				<RichTooltip
					side="top"
					delayMs={0}
					maxWidth={320}
					content={
						<ul className="m-0 list-disc pl-4 font-sans text-caption">
							<li>タイムラインをダブルクリック：工程分解点を追加</li>
							<li>分割点を選択して ←→：1フレーム単位で微調整</li>
							<li>Shift + ←→：10フレーム単位で微調整</li>
							<li>Delete / Backspace：選択中の分割点を削除</li>
							<li>分割点を右クリック：削除メニューを表示</li>
						</ul>
					}
				>
					<span
						className="inline-flex items-center justify-center rounded-2 p-0.5 text-fg-3 hover:bg-overlay-hover"
						data-testid="segmentation-help"
					>
						<Icon name="helpCircle" size={14} color="var(--fg-3)" />
					</span>
				</RichTooltip>
			</span>

			{/* タイムラインバー — ダブルクリックで工程分解点を追加 */}
			<div
				ref={barRef}
				className="relative h-8 cursor-pointer rounded-2 bg-surface-2 shadow-ring"
				onDoubleClick={handleDoubleClick}
				data-testid="timeline-bar"
			>
				<Track
					barRef={barRef}
					duration={duration}
					boundaries={boundaries}
					segments={segments}
					selectedBoundary={selectedBoundary}
					selectedSegment={selectedSegment}
					hoveredSegment={hoveredSegment}
					onMoveBoundary={handleMarkerMove}
					onContextMenu={handleBoundaryContextMenu}
					onSelectBoundary={handleSelectBoundary}
					onSelectSegment={onSelectSegment}
					onHoverSegment={onHoverSegment}
					onDragStart={onDragStart}
					onDragEnd={onDragEnd}
				/>

				<div
					className="pointer-events-none absolute top-0 z-10 h-full w-0.5"
					style={{
						left: `${playheadPosition * 100}%`,
						transform: "translateX(-50%)",
						backgroundColor: "var(--accent-solid)",
					}}
					data-testid="timeline-playhead"
				/>
			</div>

			{contextMenu && (
				<ContextMenu
					position={{ x: contextMenu.x, y: contextMenu.y }}
					onClose={() => setContextMenu(null)}
					minWidth={0}
					testId="boundary-context-menu"
					items={[
						{
							key: "remove-boundary",
							label: "削除",
							destructive: true,
							testId: "boundary-context-menu-remove",
							onSelect: handleRemoveFromContextMenu,
						},
					]}
				/>
			)}

			{/* ヒントと時刻ラベル */}
			<div className="flex justify-between font-mono text-section text-fg-3">
				<span>{formatTimecode(0, 0)}</span>
				<span>{formatTimecode(duration, 0)}</span>
			</div>
		</div>
	);
}
